import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PhotoSlide from '../PhotoSlide';
import VideoGallery from '../VideoGallery';
import CtaBanner from '../CtaBanner';
import { Product } from '../../types';
import { useI18n } from '../../i18n';
import { en, km } from './ProductBody.copy';

interface ProductBodyProps {
  product: Product;
  slug?: string;
}

const ProductBody: React.FC<ProductBodyProps> = ({ product, slug }) => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  return (
    <>
      {product.workflow && (
        <Section>
          <SectionKicker>{t.howItWorks}</SectionKicker>
          <SectionTitle>{product.workflow.title}</SectionTitle>
          <WorkflowPath>{product.workflow.path}</WorkflowPath>
          <WorkflowGrid>
            {product.workflow.steps.map((step, index) => (
              <WorkflowCard key={step.title}>
                <StepIndex>{String(index + 1).padStart(2, '0')}</StepIndex>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </WorkflowCard>
            ))}
          </WorkflowGrid>
        </Section>
      )}

      {product.videos && product.videos.length > 0 && (
        <Section>
          <SectionKicker>{t.seeIt}</SectionKicker>
          <SectionTitle>{t.productVideos}</SectionTitle>
          <VideoGallery videos={product.videos} />
        </Section>
      )}

      {product.mainFeatures?.length > 0 && (
        <Section>
          <SectionKicker>{t.insideTheModule}</SectionKicker>
          <SectionTitle>{t.keyCapabilities}</SectionTitle>
          <FeaturesGrid>
            {product.mainFeatures.map((feature) => (
              <FeatureCard key={feature.title}>
                {feature.icon && <FeatureIcon>{feature.icon}</FeatureIcon>}
                <div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  {feature.images && feature.images.length > 0 && (
                    <SlideWrap>
                      <PhotoSlide images={feature.images} />
                    </SlideWrap>
                  )}
                </div>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Section>
      )}

      {product.capabilities && product.capabilities.length > 0 && (
        <Section>
          <SectionKicker>{t.inDetail}</SectionKicker>
          <SectionTitle>{t.whatYouCanSetUp}</SectionTitle>
          <CapabilityGrid>
            {product.capabilities.map((group) => (
              <CapabilityCard key={group.title}>
                <h3>{group.title}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CapabilityCard>
            ))}
          </CapabilityGrid>
        </Section>
      )}

      {product.benefits && product.benefits.length > 0 && (
        <Section>
          <SectionKicker>{t.whyTeamsUseIt}</SectionKicker>
          <SectionTitle>{t.benefitsTitle}</SectionTitle>
          <BenefitsGrid>
            {product.benefits.map((benefit) => (
              <BenefitCard key={benefit.number}>
                <span>{benefit.number}</span>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </Section>
      )}

      {product.additionalFeatures && product.additionalFeatures.length > 0 && (
        <Section>
          <SectionKicker>{t.alsoIncluded}</SectionKicker>
          <SectionTitle>{t.platformExtras}</SectionTitle>
          <ExtrasGrid>
            {product.additionalFeatures.map((feature) => (
              <ExtraCard key={feature.title}>
                {feature.icon && <ExtraIcon>{feature.icon}</ExtraIcon>}
                <h3>{feature.title}</h3>
                {feature.description && <p>{feature.description}</p>}
              </ExtraCard>
            ))}
          </ExtrasGrid>
        </Section>
      )}

      {product.related && product.related.length > 0 && (
        <Section>
          <SectionKicker>{t.connected}</SectionKicker>
          <SectionTitle>{t.relatedTitle}</SectionTitle>
          <RelatedRow>
            {product.related.map((link) =>
              link.href ? (
                <RelatedAnchor key={link.label} href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </RelatedAnchor>
              ) : (
                <RelatedLink key={link.label} to={localePath(link.to || '/')}>
                  {link.label}
                </RelatedLink>
              )
            )}
          </RelatedRow>
        </Section>
      )}

      <CtaBanner
        title={t.ctaTitle}
        description={t.ctaDescription}
        primaryLabel={t.contactUs}
        primaryTo={localePath('/contact')}
        secondaryLabel={product.docsUrl ? t.openUserGuide : slug === 'hrms' ? t.tryLiveDemo : t.seeLiveDemos}
        secondaryHref={product.docsUrl}
        secondaryTo={!product.docsUrl ? localePath('/demo') : undefined}
      />
    </>
  );
};

const Section = styled.section`
  margin: 3.25rem 0;
`;

const SectionKicker = styled.p`
  margin: 0 0 0.4rem;
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
`;

const WorkflowPath = styled.p`
  margin: -0.5rem 0 1.5rem;
  color: var(--text-muted);
  font-weight: 600;
`;

const WorkflowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const WorkflowCard = styled.article`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.25rem 1.2rem 1.35rem;

  h3 {
    font-size: 1.1rem;
    margin: 0.35rem 0 0.5rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    line-height: 1.55;
    font-size: 0.95rem;
  }
`;

const StepIndex = styled.span`
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--primary-color);
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FeatureCard = styled.article`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: white;
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.35rem;
  box-shadow: 0 8px 24px rgba(16, 32, 51, 0.04);

  h3 {
    font-size: 1.2rem;
    margin: 0 0 0.55rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: var(--primary-light);
  font-size: 1.35rem;
`;

const SlideWrap = styled.div`
  margin-top: 1rem;
`;

const CapabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const CapabilityCard = styled.article`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.35rem 1.4rem;

  h3 {
    margin: 0 0 0.85rem;
    font-size: 1.15rem;
    color: var(--text-dark);
  }

  ul {
    margin: 0;
    padding-left: 1.1rem;
    color: var(--text-muted);
  }

  li {
    margin-bottom: 0.45rem;
    line-height: 1.5;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.article`
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.4rem 1.45rem;

  span {
    font-size: 0.85rem;
    font-weight: 800;
    color: var(--primary-color);
  }

  h3 {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const ExtrasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const ExtraCard = styled.article`
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 1.2rem 1.15rem;
  background: white;

  h3 {
    margin: 0 0 0.4rem;
    font-size: 1.05rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.92rem;
    line-height: 1.5;
  }
`;

const ExtraIcon = styled.div`
  font-size: 1.3rem;
  margin-bottom: 0.55rem;
`;

const RelatedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const relatedChip = `
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.95rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: white;
  color: var(--text-dark);
  font-weight: 600;
  font-size: 0.92rem;
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const RelatedLink = styled(Link)`
  ${relatedChip}
`;

const RelatedAnchor = styled.a`
  ${relatedChip}
`;

export default ProductBody;
